import { PermissionAssignmentName } from '@acre/graphql'
import { PermissionGateName, User } from '@acre/graphql/types'

type getUserOrganisationIdsProps = {
  user: User
}

type getUserOrganisationIdsReturnType = {
  userOrganisationId: string
  firstReviewingForHint: string
  all: string[]
}

export const getUserOrganisationIds = ({ user }: getUserOrganisationIdsProps): getUserOrganisationIdsReturnType => {
  let organisationIds: getUserOrganisationIdsReturnType = {
    userOrganisationId: '',
    firstReviewingForHint: '',
    all: [],
  }
  if (!user) return organisationIds
  const { organisation_id, organisation } = user

  if (organisation_id) {
    organisationIds.all.push(organisation_id)
    organisationIds.userOrganisationId = organisation_id
  }

  if (!organisation) return organisationIds

  const { metadata } = organisation
  if (Array.isArray(metadata) && metadata.length) {
    metadata.forEach((datum) => {
      if (datum && datum.name === 'reviewing_for_hint' && datum.string_value) {
        organisationIds.all.push(datum.string_value)
        organisationIds.firstReviewingForHint = !organisationIds.firstReviewingForHint ? datum.string_value : ''
      }
    })
  }

  return organisationIds
}

export const GATE_TO_PERMISSION: Partial<Record<PermissionGateName, PermissionAssignmentName>> = {
  [PermissionGateName.Accounting]: PermissionAssignmentName.ACCOUNTING,
  [PermissionGateName.AcreCaseImport]: PermissionAssignmentName.ACRE_CASE_IMPORT,
  [PermissionGateName.AcreOrganisationCreateRegOrg]: PermissionAssignmentName.ACRE_ORGANISATION_CREATE_REG_ORG,
  [PermissionGateName.ApplyPaymentLedger]: PermissionAssignmentName.APPLY_PAYMENT_LEDGER,
  [PermissionGateName.CaseChangeOwner]: PermissionAssignmentName.CASE_CHANGE_OWNER,
  [PermissionGateName.ClientViewProtected]: PermissionAssignmentName.CLIENT_VIEW_PROTECTED,
  [PermissionGateName.CreateCaseLedger]: PermissionAssignmentName.CREATE_CASE_LEDGER,
  [PermissionGateName.DeleteLedger]: PermissionAssignmentName.DELETE_LEDGER,
  [PermissionGateName.EditCase]: PermissionAssignmentName.CASE_EDIT_CASE,
  [PermissionGateName.EditCaseLedger]: PermissionAssignmentName.EDIT_CASE_LEDGER,
  [PermissionGateName.EditClient]: PermissionAssignmentName.CLIENT_EDIT_CLIENT,
  [PermissionGateName.EditClientRestricted]: PermissionAssignmentName.CLIENT_EDIT_CLIENT_RESTRICTED,
  [PermissionGateName.EditGeneralInsurance]: PermissionAssignmentName.REGULATED_GENERALINSURANCE,
  [PermissionGateName.EditGeneralInsuranceProvisional]: PermissionAssignmentName.REGULATED_GENERALINSURANCE_PROVISIONAL,
  [PermissionGateName.EditOrganisationCompanyInformationCompanySettings]: PermissionAssignmentName.ORGANISATION_EDIT_ORG,
  [PermissionGateName.EditOrganisationGeneralInsuranceCompanySettings]: PermissionAssignmentName.ORGANISATION_EDIT_METADATA,
  [PermissionGateName.EditOrganisationMortgageFeesCompanySettings]: PermissionAssignmentName.ORGANISATION_EDIT_FEES,
  [PermissionGateName.EditUserStatusCompanySettings]: PermissionAssignmentName.USER_EDIT_USER,
  [PermissionGateName.EditUnlinkedPaymentsLedger]: PermissionAssignmentName.EDIT_UNLINKED_PAYMENTS_LEDGER,
  [PermissionGateName.FirmManagetemplates]: PermissionAssignmentName.FIRM_MANAGETEMPLATES,
  [PermissionGateName.GroupEditGroup]: PermissionAssignmentName.GROUP_EDIT_GROUP,
  [PermissionGateName.GroupViewGroup]: PermissionAssignmentName.GROUP_VIEW_GROUP,
  [PermissionGateName.ManageOrganisation]: PermissionAssignmentName.MANAGE_ORGANISATION,
  [PermissionGateName.OrganisationCreateArOrg]: PermissionAssignmentName.ORGANISATION_CREATE_AR_ORG,
  [PermissionGateName.OrganisationCreateIntroducerOrg]: PermissionAssignmentName.ORGANISATION_CREATE_INTRODUCER_ORG,
  [PermissionGateName.OrganisationEditMetadata]: PermissionAssignmentName.ORGANISATION_EDIT_METADATA,
  [PermissionGateName.OrganisationEditOrg]: PermissionAssignmentName.ORGANISATION_EDIT_ORG,
  [PermissionGateName.OrganisationEditProtected]: PermissionAssignmentName.ORGANISATION_EDIT_PROTECTED,
  [PermissionGateName.OrganisationEditSystemMetadata]: PermissionAssignmentName.ORGANISATION_EDIT_SYSTEM_METADATA,
  [PermissionGateName.OrganisationReporting]: PermissionAssignmentName.ORGANISATION_REPORTING,
  [PermissionGateName.OrganisationView]: PermissionAssignmentName.ORGANISATION_VIEW,
  [PermissionGateName.PermissionsEditPermission]: PermissionAssignmentName.PERMISSIONS_EDIT_PERMISSION,
  [PermissionGateName.PermissionsEditRegulatedPermission]: PermissionAssignmentName.PERMISSIONS_EDIT_REGULATED_PERMISSION,
  [PermissionGateName.PermissionsViewPermissions]: PermissionAssignmentName.PERMISSIONS_VIEW_PERMISSIONS,
  [PermissionGateName.ReferralPartners]: PermissionAssignmentName.REFERRAL_PARTNERS,
  [PermissionGateName.RegulatedComplianceAddcasenotes]: PermissionAssignmentName.REGULATED_COMPLIANCE_ADDCASENOTES,
  [PermissionGateName.RegulatedComplianceApproveadvice]: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVEADVICE,
  [PermissionGateName.RegulatedComplianceApproveaml]: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVEAML,
  [PermissionGateName.RegulatedComplianceApprovecases]: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVECASES,
  [PermissionGateName.RegulatedComplianceApproveeidvoverride]: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVEEIDVOVERRIDE,
  [PermissionGateName.RegulatedComplianceViewcasedetail]: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCASEDETAIL,
  [PermissionGateName.RegulatedComplianceViewcasenotes]: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCASENOTES,
  [PermissionGateName.RegulatedDebtconsolidation]: PermissionAssignmentName.REGULATED_DEBTCONSOLIDATION,
  [PermissionGateName.RegulatedEquityrelease]: PermissionAssignmentName.REGULATED_EQUITYRELEASE,
  [PermissionGateName.RegulatedEquityreleaseProvisional]: PermissionAssignmentName.REGULATED_EQUITYRELEASE_PROVISIONAL,
  [PermissionGateName.RegulatedGeneralinsurance]: PermissionAssignmentName.REGULATED_GENERALINSURANCE,
  [PermissionGateName.RegulatedGeneralinsuranceProvisional]: PermissionAssignmentName.REGULATED_GENERALINSURANCE_PROVISIONAL,
  [PermissionGateName.RegulatedMortgage]: PermissionAssignmentName.REGULATED_MORTGAGE,
  [PermissionGateName.RegulatedMortgageProvisional]: PermissionAssignmentName.REGULATED_MORTGAGE_PROVISIONAL,
  [PermissionGateName.RegulatedProtectioninsurance]: PermissionAssignmentName.REGULATED_PROTECTIONINSURANCE,
  [PermissionGateName.RegulatedProtectioninsuranceProvisional]: PermissionAssignmentName.REGULATED_PROTECTIONINSURANCE_PROVISIONAL,
  [PermissionGateName.UserCreateUser]: PermissionAssignmentName.USER_CREATE_USER,
  [PermissionGateName.UserEditAuthentication]: PermissionAssignmentName.USER_EDIT_AUTHENTICATION,
  [PermissionGateName.UserEditUser]: PermissionAssignmentName.USER_EDIT_USER,
  [PermissionGateName.UserEditUserMetadata]: PermissionAssignmentName.USER_EDIT_USER_METADATA,
  [PermissionGateName.ViewBrokerDashboard]: PermissionAssignmentName.CASE_VIEW_CASE,
  [PermissionGateName.ViewCaseCompliance]: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCASEDETAIL,
  [PermissionGateName.ViewCaseFlags]: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCASENOTES,
  [PermissionGateName.ViewCaseSection]: PermissionAssignmentName.CASE_VIEW_CASE,
  [PermissionGateName.ViewClientSection]: PermissionAssignmentName.CLIENT_VIEW_CLIENT,
  [PermissionGateName.ViewMiDashboard]: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCOMPLIANCEDASHBOARD,
  [PermissionGateName.ViewOverrideClientEidv]: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVEEIDVOVERRIDE,
  [PermissionGateName.ViewUnlinkedPaymentsLedger]: PermissionAssignmentName.VIEW_UNLINKED_PAYMENTS_LEDGER,
  [PermissionGateName.WriteOffLedger]: PermissionAssignmentName.WRITE_OFF_LEDGER,
}
